import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Savings as RoomIcon,
} from '@mui/icons-material';
import { MainLayout } from '../components/MainLayout';
import {
  ContributionLimitsService,
  type ContributionLimit,
  type ContributionLimitRequest,
  type ContributionProgress,
} from '../services/contribution-limits-service';
import { accountsService } from '../services/accounts-service';

interface AccountOption {
  id: string;
  name: string;
  currency: string;
}

/**
 * Registered-account room tracking (TFSA / RRSP / FHSA).
 *
 * One limit spans a SET of accounts because room is per person per plan
 * type, not per broker account. Withdrawals are shown but never netted
 * against contributions: TFSA room only comes back the following January.
 */
export const ContributionRoomPage: React.FC = () => {
  const [limits, setLimits] = useState<ContributionLimit[]>([]);
  const [progress, setProgress] = useState<Record<string, ContributionProgress>>({});
  const [accounts, setAccounts] = useState<AccountOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<ContributionLimit | null>(null);
  const [saving, setSaving] = useState(false);
  const currentYear = new Date().getFullYear();
  const [form, setForm] = useState<ContributionLimitRequest>({
    group_name: '',
    contribution_year: currentYear,
    limit_amount: '',
    currency: 'CAD',
    start_date: `${currentYear}-01-01`,
    account_ids: [],
  });

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [ls, accountsResp] = await Promise.all([
        ContributionLimitsService.list(),
        accountsService.getAccounts({ per_page: 200 } as any),
      ]);
      setLimits(ls);
      const accts = (accountsResp as any)?.data?.data ?? (accountsResp as any)?.data ?? [];
      setAccounts(
        (Array.isArray(accts) ? accts : []).map((a: any) => ({
          id: a.id,
          name: a.display_name || a.name,
          currency: a.currency,
        })),
      );
      const entries = await Promise.all(
        ls.map(async (l) => [l.id, await ContributionLimitsService.progress(l.id)] as const),
      );
      setProgress(Object.fromEntries(entries));
    } catch (e: any) {
      setError(e?.message || 'Failed to load contribution limits');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const openCreate = () => {
    setEditing(null);
    setForm({
      group_name: '',
      contribution_year: currentYear,
      limit_amount: '',
      currency: 'CAD',
      start_date: `${currentYear}-01-01`,
      account_ids: [],
    });
    setDialogOpen(true);
  };

  const openEdit = (l: ContributionLimit) => {
    setEditing(l);
    setForm({
      group_name: l.group_name,
      contribution_year: l.contribution_year,
      limit_amount: l.limit_amount,
      currency: l.currency,
      start_date: l.start_date.slice(0, 10),
      end_date: l.end_date ? l.end_date.slice(0, 10) : undefined,
      account_ids: l.account_ids,
    });
    setDialogOpen(true);
  };

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      if (editing) {
        await ContributionLimitsService.update(editing.id, form);
      } else {
        await ContributionLimitsService.create(form);
      }
      setDialogOpen(false);
      await load();
    } catch (e: any) {
      setError(e?.message || 'Failed to save limit');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (l: ContributionLimit) => {
    if (!window.confirm(`Delete "${l.group_name}"? Ledger entries are untouched.`)) return;
    try {
      await ContributionLimitsService.delete(l.id);
      await load();
    } catch (e: any) {
      setError(e?.message || 'Failed to delete limit');
    }
  };

  const accountName = (id: string) => accounts.find((a) => a.id === id)?.name ?? '(account)';

  const fmt = (s: string, currency: string) =>
    new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(Number(s));

  return (
    <MainLayout>
      <Box sx={{ p: 3 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <RoomIcon color="primary" />
            <Typography variant="h5">Contribution room</Typography>
          </Stack>
          <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
            New limit
          </Button>
        </Stack>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: 720 }}>
          Track TFSA, RRSP and FHSA room against the deposits actually recorded in your
          ledger. Transfers between accounts inside the same limit don&apos;t count, and
          neither does growth earned inside the account &mdash; only new money in.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : limits.length === 0 ? (
          <Card variant="outlined">
            <CardContent>
              <Typography color="text.secondary">
                No limits yet. Create one per plan and year &mdash; e.g. &ldquo;TFSA{' '}
                {currentYear}&rdquo; with your TFSA accounts and this year&apos;s room.
              </Typography>
            </CardContent>
          </Card>
        ) : (
          <Stack spacing={2}>
            {limits.map((l) => {
              const p = progress[l.id];
              const used = p ? Number(p.total_contributions_base) : 0;
              const limit = Number(l.limit_amount);
              const pct = limit > 0 ? Math.min(100, (used / limit) * 100) : 0;
              const over = p && Number(p.remaining_base) < 0;
              return (
                <Card key={l.id} variant="outlined">
                  <CardContent>
                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                      <Box>
                        <Typography variant="h6">{l.group_name}</Typography>
                        <Stack direction="row" spacing={1} sx={{ mt: 0.5, flexWrap: 'wrap' }}>
                          <Chip size="small" label={l.contribution_year} />
                          {l.account_ids.map((id) => (
                            <Chip key={id} size="small" variant="outlined" label={accountName(id)} />
                          ))}
                        </Stack>
                      </Box>
                      <Stack direction="row">
                        <Tooltip title="Edit">
                          <IconButton size="small" onClick={() => openEdit(l)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton size="small" onClick={() => void remove(l)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </Stack>

                    {p && (
                      <Box sx={{ mt: 2 }}>
                        <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
                          <Typography variant="body2">
                            {fmt(p.total_contributions_base, l.currency)} of{' '}
                            {fmt(l.limit_amount, l.currency)} used
                          </Typography>
                          <Typography
                            variant="body2"
                            color={over ? 'error' : 'text.secondary'}
                            fontWeight={over ? 700 : 400}
                          >
                            {over
                              ? `OVER by ${fmt(String(-Number(p.remaining_base)), l.currency)}`
                              : `${fmt(p.remaining_base, l.currency)} room left`}
                          </Typography>
                        </Stack>
                        <LinearProgress
                          variant="determinate"
                          value={pct}
                          color={over ? 'error' : pct > 90 ? 'warning' : 'primary'}
                          sx={{ height: 8, borderRadius: 4 }}
                        />
                        {Number(p.total_withdrawals_base) > 0 && (
                          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                            Withdrawn this window: {fmt(p.total_withdrawals_base, l.currency)}{' '}
                            (room returns next January for TFSA; never for RRSP)
                          </Typography>
                        )}
                      </Box>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </Stack>
        )}

        <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>{editing ? 'Edit limit' : 'New contribution limit'}</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <TextField
                label="Name"
                placeholder={`TFSA ${currentYear}`}
                value={form.group_name}
                onChange={(e) => setForm({ ...form, group_name: e.target.value })}
                fullWidth
              />
              <Stack direction="row" spacing={2}>
                <TextField
                  label="Year"
                  type="number"
                  value={form.contribution_year}
                  onChange={(e) => setForm({ ...form, contribution_year: Number(e.target.value) })}
                  sx={{ width: 120 }}
                />
                <TextField
                  label="Limit amount"
                  value={form.limit_amount}
                  onChange={(e) => setForm({ ...form, limit_amount: e.target.value })}
                  fullWidth
                />
              </Stack>
              <Stack direction="row" spacing={2}>
                <TextField
                  label="Window start"
                  type="date"
                  value={form.start_date}
                  onChange={(e) => setForm({ ...form, start_date: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  helperText="RRSP: use Mar 2 of the prior year to catch first-60-days deposits"
                />
                <TextField
                  label="Window end (optional)"
                  type="date"
                  value={form.end_date ?? ''}
                  onChange={(e) =>
                    setForm({ ...form, end_date: e.target.value || undefined })
                  }
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                />
              </Stack>
              <TextField
                select
                label="Accounts in this limit"
                value={form.account_ids}
                onChange={(e) =>
                  setForm({
                    ...form,
                    account_ids:
                      typeof e.target.value === 'string'
                        ? e.target.value.split(',')
                        : (e.target.value as unknown as string[]),
                  })
                }
                SelectProps={{ multiple: true }}
                helperText="All accounts sharing this room, across brokers"
                fullWidth
              >
                {accounts.map((a) => (
                  <MenuItem key={a.id} value={a.id}>
                    {a.name} ({a.currency})
                  </MenuItem>
                ))}
              </TextField>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button
              variant="contained"
              onClick={() => void save()}
              disabled={saving || !form.group_name || !form.limit_amount || form.account_ids.length === 0}
            >
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </MainLayout>
  );
};
